import { computeEditHistoryEntries } from "../../App/EditHistory"
import { IAuditLogEdit, IAuditLogEditSource, IDay, ILog, ILogChange, ILogChangeAction, ILogChangeType, Log } from "../../Types"

// The three `DayEditableField`s a day-edit log entry can announce, mapped to the
// change type the renderer translates with. Mirrors `Day.rosterEditableFields` in
// `Sources/LogbookApp/DayApi.swift`.
const rosterChangeTypes: Record<string, ILogChangeType> = {
  crew: ILogChangeType.crew,
  passengers: ILogChangeType.passengers,
  skipper: ILogChangeType.skipper,
};

// A roster field's value is either a list of ship-user ids (crew/passengers), a
// single id (skipper), or unset. Normalise all three to a list so one diff covers
// them. Anything else (a malformed/legacy value) contributes nobody rather than
// throwing.
function shipUserIds(value: unknown): number[] {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is number => typeof entry === "number");
  }
  return typeof value === "number" ? [value] : [];
}

// Who came aboard or left, as announced by a day-edit log entry (issue #1285).
//
// The entry itself stores no copy of the change -- only the ids of the
// `AuditLogEdit` rows it was created for (`LogMetadata.auditLogEditIds`). This
// resolves those rows out of the day's edit history and diffs each one's
// previous value against its new value, which `computeEditHistoryEntries` derives
// the same way the edit-history UI does: the next chronological edit of that field,
// or the day's own current value for the latest one. That keeps a single source of
// truth -- the audit trail -- instead of a second, independently-computed snapshot
// that could disagree with it (which is exactly what went wrong before #1285).
//
// Returns `[]` for a log that announces nothing, so callers can fall back to the
// legacy `LogMetadata.changes` snapshot for entries recorded before the switch.
export function logRosterChanges(log: ILog, day?: IDay, auditLogEdits?: IAuditLogEdit[]): ILogChange[] {
  const referencedIds = Log.parse(log).dataParsed.auditLogEditIds;
  if (!referencedIds || referencedIds.length == 0 || !day || !auditLogEdits) {
    return [];
  }
  const dayEdits = auditLogEdits.filter((edit) => edit.sourceType == IAuditLogEditSource.day && edit.sourceId == day.id && rosterChangeTypes[edit.field] != undefined);
  const entries = computeEditHistoryEntries(dayEdits, (field) => (day as unknown as Record<string, unknown>)[field]);
  const changes: ILogChange[] = [];
  for (const id of referencedIds) {
    const entry = entries.find((e) => e.id == id);
    const type = entry && rosterChangeTypes[entry.field];
    if (!entry || type == undefined) {
      continue;
    }
    const previous = shipUserIds(entry.previousValue);
    const current = shipUserIds(entry.newValue);
    const removals = previous.filter((i) => !current.includes(i))
      .map((shipUserId) => ({ action: ILogChangeAction.remove, type, id: shipUserId }));
    const additions = current.filter((i) => !previous.includes(i))
      .map((shipUserId) => ({ action: ILogChangeAction.add, type, id: shipUserId }));
    changes.push(...(type == ILogChangeType.skipper ? [...additions, ...removals] : [...removals, ...additions]));
  }
  return changes;
}
